import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkles, Code, Cpu, Award } from 'lucide-react';

const words = ['SaaS Platforms', 'Full-Stack Apps', 'Custom Software', 'Salon Systems'];

export default function Hero() {
  const [activeWordIndex, setActiveWordIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveWordIndex((prev) => (prev + 1) % words.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const scrollToSection = (id) => {
    const element = document.querySelector(id);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', damping: 25, stiffness: 100 }
    }
  };

  const stats = [
    { label: 'Success Rate', value: '99%', icon: Award, color: 'text-[#8B6B4A]' },
    { label: 'Clean Code Quality', value: '100%', icon: Code, color: 'text-[#C8A97E]' },
    { label: 'AI & Automation Ready', value: 'True', icon: Cpu, color: 'text-[#6B625B]' }
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-[#F8F5F0] via-[#F4EDE3] to-[#EFE7DD] pt-28 pb-16" data-testid="hero-section">
      {/* Background Radial Gradients & Glow Blobs */}
      <div className="absolute top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2 glowing-blob blob-gold opacity-[0.08] animate-pulse-slow" />
      <div className="absolute bottom-1/3 right-1/4 translate-x-1/2 glowing-blob blob-brown opacity-[0.06] animate-pulse-slow-reverse" />
      
      {/* Soft Luxury Grid Overlay */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA4MCAwIEwgMCAwIDAgODAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgxMzksMTA3LDc0LDAuMDMpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-80 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 w-full">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="text-center space-y-8 max-w-4xl mx-auto"
        >
          {/* Badge */}
          <motion.div variants={itemVariants} className="inline-flex justify-center">
            <div className="inline-flex items-center gap-2 bg-[#8B6B4A]/5 border border-[#8B6B4A]/10 rounded-full px-5 py-2 backdrop-blur-md">
              <Sparkles className="w-4 h-4 text-[#C8A97E] animate-pulse" />
              <span className="text-xs font-semibold tracking-wider uppercase text-[#8B6B4A] font-poppins">
                Elite Software Design Studio
              </span>
            </div>
          </motion.div>

          {/* Heading */}
          <motion.h1
            variants={itemVariants}
            className="text-4xl sm:text-6xl md:text-7xl font-bold tracking-tight text-[#3E2F24] font-space leading-[1.1]"
            data-testid="hero-headline"
          >
            We Engineer Bespoke
            <br />
            <span className="relative inline-block h-[1.25em] w-full pt-1">
              <AnimatePresence mode="wait">
                <motion.span
                  key={activeWordIndex}
                  initial={{ y: 15, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -15, opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute left-0 right-0 text-transparent bg-clip-text bg-gradient-to-r from-[#8B6B4A] to-[#C8A97E] filter drop-shadow-[0_1px_4px_rgba(139,107,74,0.05)]"
                >
                  {words[activeWordIndex]}
                </motion.span>
              </AnimatePresence>
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            variants={itemVariants}
            className="text-base sm:text-lg md:text-xl text-[#6B625B] max-w-2xl mx-auto font-poppins leading-relaxed"
            data-testid="hero-subheadline"
          >
            Code Credence crafts warm, elegant web experiences, custom subscription platforms, and high-performance management systems with an expensive, minimal design aesthetic.
          </motion.p>

          {/* CTAs */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4"
            data-testid="hero-cta-group"
          >
            <button
              onClick={() => scrollToSection('#contact')}
              className="relative group overflow-hidden bg-[#8B6B4A] hover:bg-[#3E2F24] text-white rounded-full px-8 py-4 font-semibold transition-all duration-300 shadow-md hover:shadow-[#8B6B4A]/10 active:scale-95 flex items-center gap-2 font-poppins text-base w-full sm:w-auto justify-center"
              data-testid="hero-talk-expert-btn"
            >
              <span className="relative z-10 flex items-center gap-2">
                Launch Your Project
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
              <span className="absolute inset-0 bg-[#3E2F24] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
            </button>
            
            <button
              onClick={() => scrollToSection('#portfolio')}
              className="relative group overflow-hidden bg-white/40 border border-[#8B6B4A]/20 hover:border-[#8B6B4A]/55 text-[#3E2F24] hover:text-[#8B6B4A] rounded-full px-8 py-4 font-semibold transition-all active:scale-95 font-poppins text-base w-full sm:w-auto justify-center backdrop-blur-md"
              data-testid="hero-view-portfolio-btn"
            >
              Explore Solutions
            </button>
          </motion.div>

          {/* Stats Bar */}
          <motion.div
            variants={itemVariants}
            className="pt-12"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto bg-white/40 border border-[#8B6B4A]/10 rounded-3xl p-6 shadow-sm backdrop-blur-md">
              {stats.map((stat, idx) => (
                <div key={idx} className="flex items-center gap-4 px-4 py-2 justify-center md:justify-start border-b md:border-b-0 md:border-r border-[#8B6B4A]/10 last:border-0">
                  <div className={`p-3 bg-[#8B6B4A]/5 rounded-2xl ${stat.color}`}>
                    <stat.icon className="w-6 h-6" />
                  </div>
                  <div className="text-left">
                    <div className="text-2xl font-bold font-space text-[#3E2F24]">{stat.value}</div>
                    <div className="text-xs font-poppins text-[#6B625B]">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}